/**
 * @date: 2022-04-18
 * @author: Kevin Hsiau <khsiau@max.md>
 * @copy: (C) Copyright 2022 MaxMD Corporation, All Rights Reserved.
 */

import React, {useEffect, useState} from 'react'
import {Link, useLocation, useParams} from "react-router-dom";
import {getQueryParams, handleHttpResponse} from "../utils/http-helpers";
import {Alert, Button, Pagination} from "@mui/material";
import ShowSnackbar from "../utils/ShowSnackbar";
import {CustomBackdrop, StyledContainer} from "../utils/customStyles";
import {ApiUrl, PortalUrl} from "../utils/portal-urls";
import {convertDateToTimeString} from "../utils/cert-helper";
import AdHocReportBinaryList from "./AdHocReportBinaryList";
import AdHocReportSearchPanel from "./AdHocReportSearchPanel";
import ArrowBackIosIcon from "@mui/icons-material/ArrowBackIos";


export const getPath = () => {
    const obj = {};
    const location = useLocation();
    const path = location.pathname;
    if (path.length === 0) {
        console.error('No path found');
    }
    console.log("Current path: " + path);
    const pathArray = path.split('/').slice(1)
    obj['id'] = pathArray[pathArray.length - 1];
    obj['type'] = pathArray[pathArray.length - 2];
    // if (pathArray.includes('contribution')) {
    //     obj['type'] = 'contribution';
    // } else if (pathArray.includes('publication')) {
    //     obj['type'] = 'publication';
    // }
    return obj;
}

export default function AdHocReportHome () {

    const getParameters = getQueryParams();
    const { id } = useParams();
    const path = getPath();


    const [loading, setLoading] = useState(false);
    const [binaryLoading, setBinaryLoading] = useState(false);
    const [refresh, setRefresh] = useState(new Date().getTime());
    const [page, setPage] = useState(getParameters.page ? parseInt(getParameters.page) : 0);
    const [pageResult, setPageResult] = useState(null);
    const [query, setQuery] = useState("");
    const [queryCount, setQueryCount] = useState(0);
    const [currentViewingQuery, setCurrentViewingQuery] = useState(null);
    const [snackbarMsg, setSnackbarMsg] = useState({open: false, msg:'', msgLv: ''});
    const [info, setInfo] = useState(null);

    useEffect( () => {
        console.log("page changed or refresh, useEffect fetch: ", query, page);
        if (query !== currentViewingQuery && queryCount > 0 && page > 0) {
            setPage(0);
        } else {
            fetchInfo(id);
            // if (state?.type === 'contribution') {
            if (path?.type === 'contribution') {
                // fetchBinaryPage(query, page, `&hispId=${state.hispName}&contributionId=${id}`);
                fetchBinaryPage(query, page, `&contributionId=${id}`);
            } else if (path?.type === 'publication') {
                fetchBinaryPage(query, page, `&publishId=${id}`);
            }
        }
    }, [query, page, refresh])

    const fetchInfo = async (id) => {

        let url = '';
        if (path?.type === 'publication') {
            url = ApiUrl.Published.getPublicationById.getPath(id)
            console.log(`GET publication from: ${url}`);
        } else if (path?.type === 'contribution') {
            url = ApiUrl.Contributions.getContributionById.getPath(id);
            console.log(`GET contribution from: ${url}`);
        }

        try {
            setLoading(true);
            const res = await fetch(url);
            const data = await handleHttpResponse(res);
            setInfo(data);
        } catch (err) {
            console.error("Failed to get with AdHoc report with id = " + id + ". " + err);
            setSnackbarMsg({open: true, msg: 'Failed to Get Publication', msgLv: 'error'});
        } finally {
            setLoading(false);
        }
    }

    const fetchBinaryPage = async (query, page, hispName) => {
        const url = ApiUrl.Published.getBinaries.getPath(query, page, hispName)
        console.log("GET Binaries from: " + url);
        try {
            setBinaryLoading(true);
            const res = await fetch(url);
            const data = await handleHttpResponse(res);
            setCurrentViewingQuery(query);
            setPageResult(data);
        } catch (err) {
            console.error("Failed to get binary" + err);
            setSnackbarMsg({open: true, msg: 'Failed to Get Binaries', msgLv: 'error'});
        } finally {
            setBinaryLoading(false);
        }
    }

    return (
        <StyledContainer maxWidth='xl'>
            {info?.publish &&
                 <Button startIcon={<ArrowBackIosIcon />} variant='text' component={Link} to={PortalUrl.Published.Home.getPath()}>Publication</Button>
            }
            {info?.contribution &&
                <Button startIcon={<ArrowBackIosIcon />} variant='text' component={Link} to={PortalUrl.Contributions.Home.getPath()}>Contribution</Button>
            }

            { info?.publish &&
                <>
                    <h3 className="title">
                        Publication({convertDateToTimeString(info.publish.publishedTs)})
                        <span style={{float: 'right', fontSize: 10, color: '#337AB799'}}>ID: {info.publish.id}</span>
                    </h3>
                    <AdHocReportSearchPanel
                        onRefresh={(r) => setRefresh(r)}
                        onQueryStringChanged={(q) => setQuery(q)}
                        setPage={(p) => setPage(p)}
                        info={info.publish}
                        type='publish'
                    />
                </>
            }
            { info?.contribution &&
                <>
                    <h3 className="title">
                        Contribution ({convertDateToTimeString(info.contribution.submissionTs)})
                        <span style={{float: 'right', fontSize: 10, color: '#337AB799'}}>ID: {info.contribution.id}</span>
                    </h3>
                    <AdHocReportSearchPanel
                        onRefresh={(r) => setRefresh(r)}
                        onQueryStringChanged={(q) => setQuery(q)}
                        setPage={(p) => setPage(p)}
                        info={info.contribution}
                        type='contribution'
                    />
                </>
            }
            <hr/>
            <div className='binary-list-wrap'>
                {pageResult &&
                    <Alert severity='info' icon={false}>
                        Found {pageResult.totalElements} record(s).&nbsp;
                        {pageResult.totalPages > 1 && `Showing page ${pageResult.pageable.pageNumber + 1} (${pageResult.pageable.offset + 1} ... ${pageResult.pageable.offset + pageResult.pageable.pageSize})`}
                    </Alert>
                }
                {pageResult?.content?.length > 0 &&
                    <div>
                        <AdHocReportBinaryList binaries={pageResult.content} />
                        <br/>
                        {pageResult.totalPages > 0 &&
                            <Pagination page={page + 1} count={pageResult.totalPages} variant="outlined" color="primary" onChange={(e, newPage) => setPage(newPage - 1)}/>}
                    </div>

                }
            </div>

            <ShowSnackbar open={snackbarMsg.open} close={(c) => setSnackbarMsg(c)} msg={snackbarMsg} />
            <CustomBackdrop open={loading || binaryLoading} />
        </StyledContainer>
    )
}
